/**
 * Quota Service - Handles quota display and token estimation
 */

import { apiConfig } from "../config/apiConfig.js";
import { getCurrentUserEmail, getApiToken } from "./authService.js";
import { preparePromptData } from "./promptService.js";
import { estimateTokens, appendMessage } from "../utils/helpers.js";
import { isExcelHost } from "../config/hostConfig.js";

let currentQuota = null;

export function getCurrentQuota() {
  return currentQuota;
}

export function clearQuota() {
  currentQuota = null;
}

export function getRagFeatures() {
  return currentQuota?.features || [];
}

export async function displayQuota() {
  const currentUserEmail = getCurrentUserEmail();
  if (!currentUserEmail) return;
  
  try {
    const url = `${apiConfig.getUrl('quota')}/${encodeURIComponent(currentUserEmail)}`;
    let authHeader = {};
    try { authHeader = { Authorization: `Bearer ${await getApiToken()}` }; } catch { /* not yet signed in */ }
    const response = await fetch(url, { headers: authHeader });
    
    if (response.ok) {
      const quota = await response.json();
      updateQuotaDisplay(quota);
    } else {
      console.error('Quota fetch failed:', response.status, response.statusText);
    }
  } catch (error) {
    console.error("Error fetching quota:", error);
  }
}

export function updateQuotaDisplay(quota) {
  currentQuota = quota;
  
  const { daily, monthly } = quota;
  
  // Calculate percentage based on usage, not remaining
  const dailyUsedPercentage = (daily.tokens_used / daily.tokens_limit) * 100;
  const dailyPercentage = 100 - dailyUsedPercentage;
  
  const monthlyUsedPercentage = (monthly.tokens_used / monthly.tokens_limit) * 100;
  const monthlyPercentage = 100 - monthlyUsedPercentage;
  
  // Determine which limit is more restrictive based on remaining tokens
  const isMonthlyLimit = monthly.tokens_remaining <= daily.tokens_remaining;
  const limitLabel = isMonthlyLimit ? 'Monatskontingent' : 'Tageskontingent';
  const percentage = isMonthlyLimit ? monthlyPercentage : dailyPercentage;
  
  // Get or create quota container
  let quotaContainer = document.getElementById("quota-info");
  if (!quotaContainer) {
    quotaContainer = document.createElement("div");
    quotaContainer.id = "quota-info";
    quotaContainer.className = "quota-info";
    
    // Try to insert in user-details, fallback to chat container
    const userDetails = document.querySelector(".user-details");
    if (userDetails) {
      userDetails.appendChild(quotaContainer);
    } else {
      const chatContainer = document.getElementById("chat-interface");
      if (chatContainer) {
        chatContainer.insertBefore(quotaContainer, chatContainer.firstChild);
      } else {
        console.error('chat-interface not found!');
      }
    }
  }
  
  // Update HTML - Shows quota as percentage (with clickable button for details)
  quotaContainer.innerHTML = `
    <button id="show-quota-btn" class="quota-label quota-button">
      Verfügbares ${limitLabel}: ${percentage.toFixed(1).replace('.', ',')}%
    </button>
    <div class="quota-bar-container">
      <div class="quota-bar" style="width: ${percentage}%"></div>
    </div>
  `;
  
  // Set color based on percentage
  const progressBar = quotaContainer.querySelector('.quota-bar');
  progressBar.style.backgroundColor = percentage < 30 ? "red" : percentage < 50 ? "orange" : "green";
  
  // Add click handler to show detailed quota
  const showQuotaBtn = document.getElementById('show-quota-btn');
  if (showQuotaBtn) {
    showQuotaBtn.onclick = showDetailedQuota;
  }
  
  // Show info message for domain quotas
  if (quota.type === 'domain' && !localStorage.getItem('domain-quota-info-shown')) {
    appendMessage("ℹ️ Du nutzt ein gemeinsames Token-Kontingent mit anderen Nutzern deiner Domain.", false);
    localStorage.setItem('domain-quota-info-shown', 'true');
  }
}

export function showDetailedQuota() {
  const currentUserEmail = getCurrentUserEmail();
  
  if (!currentQuota) {
    appendMessage("ℹ️ Keine Quota-Informationen verfügbar.", false);
    return;
  }
  
  // Build quota type label with domain name if it's a shared quota
  let quotaType;
  if (currentQuota.type === 'domain') {
    const domain = currentUserEmail ? currentUserEmail.split('@')[1] : 'unbekannt';
    quotaType = `Gemeinsames Kontingent (${domain})`;
  } else {
    quotaType = `Persönliches Kontingent (${currentUserEmail})`;
  }
  
  const daily = currentQuota.daily;
  const monthly = currentQuota.monthly;
  
  // Build message with proper line breaks
  const lines = [
    `📊 ${quotaType}`,
    '',
    'Heute:',
    `  • ${daily.tokens_used.toLocaleString('de-DE')} / ${daily.tokens_limit.toLocaleString('de-DE')} Tokens verwendet`,
    `  • ${daily.requests_made} Anfrage${daily.requests_made !== 1 ? 'n' : ''} gemacht`,
    `  • ${daily.tokens_remaining.toLocaleString('de-DE')} Tokens verbleibend`,
    '',
    'Diesen Monat:',
    `  • ${monthly.tokens_used.toLocaleString('de-DE')} / ${monthly.tokens_limit.toLocaleString('de-DE')} Tokens verwendet`,
    `  • ${monthly.requests_made} Anfrage${monthly.requests_made !== 1 ? 'n' : ''} gemacht`,
    `  • ${monthly.tokens_remaining.toLocaleString('de-DE')} Tokens verbleibend`
  ];
  
  if (currentQuota.limits && currentQuota.limits.max_tokens_per_request) {
    lines.push('');
    lines.push('Limits:');
    lines.push(`  • Max. Tokens pro Anfrage: ${currentQuota.limits.max_tokens_per_request.toLocaleString('de-DE')}`);
  }

  appendMessage(lines.join('\n'), false);
}

export async function updateEstimatedUsage() {
  const estimatedDiv = document.getElementById("estimated-usage");
  const currentUserEmail = getCurrentUserEmail();
  
  if (!estimatedDiv || !currentQuota || !currentUserEmail) {
    if (estimatedDiv) estimatedDiv.textContent = "";
    return;
  }

  const chatInput = document.getElementById("chat-input");
  const message = chatInput.value.trim();
  
  if (!message) {
    estimatedDiv.textContent = "";
    return;
  }

  if (!isExcelHost()) {
    // In standalone mode, show a simple estimate based on message length only
    const estimatedTokensCount = estimateTokens(message);
    if (currentQuota) {
      const dailyPercentage = (estimatedTokensCount / currentQuota.daily.tokens_limit) * 120;
      estimatedDiv.textContent = `\u23F3 Geschätzter Verbrauch: ~${dailyPercentage.toFixed(1).replace('.', ',')}%`;
      estimatedDiv.style.color = dailyPercentage > 5 ? "#E6003C" : dailyPercentage > 1 ? "#986f0b" : "#107c10";
    }
    return;
  }

  try {
    await Excel.run(async (context) => {
      const selectedRange = context.workbook.getSelectedRange();
      const sheet = selectedRange.worksheet;
      
      const promptData = await preparePromptData(sheet, selectedRange, message);
      
      if (!promptData) {
        estimatedDiv.textContent = "";
        return;
      }

      if (promptData.error) {
        estimatedDiv.textContent = `⚠️ Zu viele Zeilen ausgewählt (max. 500)`;
        estimatedDiv.style.color = "#E6003C";
        return;
      }
      
      const estimatedTokensCount = estimateTokens(promptData.systemPrompt + promptData.userPrompt);
      const dailyPercentage = (estimatedTokensCount / currentQuota.daily.tokens_limit) * 120;
      
      // Check if exceeds per-request limit
      if (currentQuota.limits && currentQuota.limits.max_tokens_per_request && estimatedTokensCount > currentQuota.limits.max_tokens_per_request) {
        estimatedDiv.textContent = `⚠️ ~${dailyPercentage.toFixed(1).replace('.', ',')}% - Überschreitet Anfragelimit!`;
        estimatedDiv.style.color = "#E6003C";
        return;
      }
      
      estimatedDiv.textContent = `⏳ Geschätzter Verbrauch: ~${dailyPercentage.toFixed(1).replace('.', ',')}%`;
      
      // Color based on percentage
      if (dailyPercentage > 5) {
        estimatedDiv.style.color = "#E6003C";
      } else if (dailyPercentage > 1) {
        estimatedDiv.style.color = "#986f0b";
      } else {
        estimatedDiv.style.color = "#107c10";
      }
    });
  } catch (error) {
    console.error("Error updating estimate:", error);
    estimatedDiv.textContent = "";
  }
}